import { useContext, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import clsx from 'clsx';
import * as R from 'ramda';

import AddSummaryModal from '@atom/components/common/workOrderDetail/workOrderSummary/AddSummaryModal';
import WorkOrderContext from '@atom/components/workOrderDetail/WorkOrderContext';
import { WORK_ORDER_UPDATE } from '@atom/graph/work';
import { Icon, IconButton } from '@atom/mui';
import {
  Summary,
  WorkOrderUpdate,
  WorkOrderUpdateInput,
} from '@atom/types/work';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';

import SummaryRow from './SummaryRow';

import classes from './workOrderInfo.module.css';

const SummaryPane = () => {
  const { workOrderDetail, refetch } = useContext(WorkOrderContext);

  const [open, setOpen] = useState<boolean>(false);
  const [summary, setSummary] = useState<Summary[]>(
    workOrderDetail.summary || [],
  );

  const [updateSummary] = useMutation<
    { workOrderUpdate: WorkOrderUpdate },
    { workOrder: WorkOrderUpdateInput }
  >(WORK_ORDER_UPDATE);

  useEffect(() => {
    setSummary(workOrderDetail.summary);
  }, [workOrderDetail.summary]);

  const handleUpdateSummary = async (updatedSummary: Summary[]) => {
    setSummary(updatedSummary);

    await updateSummary({
      variables: {
        workOrder: {
          id: workOrderDetail.id,
          summary: updatedSummary,
        },
      },
    });

    refetch();
  };

  const addSummary = (newSummary: Summary) => {
    handleUpdateSummary([...summary, newSummary]);
  };

  const canAddSummary = hasRolePermissions(ROLE_SETS.INSPECTOR);

  return (
    <>
      <div className={classes.sectionHeader}>
        <div>Summary</div>
        {canAddSummary && (
          <IconButton onClick={() => setOpen(true)}>
            <Icon>add</Icon>
          </IconButton>
        )}
      </div>
      <div className={clsx(classes.sectionContent, classes.summary)}>
        {R.isEmpty(summary) ? (
          <div className={classes.noSummary}>There is no summary.</div>
        ) : (
          summary.map((summaryItem: Summary, index: number) => (
            <SummaryRow
              key={`${summaryItem.title}-${index}`}
              summary={summaryItem}
              summaries={summary}
              index={index}
              updateSummary={handleUpdateSummary}
            />
          ))
        )}
      </div>
      <AddSummaryModal
        open={open}
        onClose={() => setOpen(false)}
        addSummary={addSummary}
      />
    </>
  );
};

export default SummaryPane;
